import * as vision from '@mediapipe/tasks-vision';
import VideoTransformer from './VideoTransformer';
import { VideoTransformerInitOptions } from './types';

/** Размер кадра, на котором работает сегментация. Совпадает с входом модели selfie_segmenter. */
const SEGMENTATION_SIZE = 256;
/** Вес текущего кадра во временном сглаживании маски (EMA). Остальное — память прошлого. */
const MASK_SMOOTHING_FACTOR = 0.7;
/** Вес текущего кадра при движении: свежая маска важнее, шлейф за рукой короче. */
const MASK_SMOOTHING_MOTION_FACTOR = 0.92;
/** Средний сдвиг маски между кадрами, выше которого считается движением. */
const MASK_MOTION_THRESHOLD = 0.02;
/** Контрастная рампа уверенности: ниже — фон, выше — человек. Середина уплотняется. */
const MASK_LO = 0.25;
const MASK_HI = 0.6;
/** Уверенная сердцевина: отсюда край лица восстанавливается после эрозии. */
const MASK_CORE_LO = 0.85;
const MASK_CORE_HI = 0.95;
/** Ширина низкого канваса, на котором считается заблюренный фон. Цена блюра растёт с пикселями. */
const BG_LOW_WIDTH = 320;
/** Заблюренный фон пересчитывается каждый второй кадр, человек рисуется на всех кадрах. */
const BG_UPDATE_EVERY = 2;
/** Индекс класса «человек» по карте модели selfie_segmenter: 0 — фон, 1 — человек. */
const DEFAULT_PERSON_CLASS_INDEX = 1;
export type SegmenterOptions = Partial<vision.ImageSegmenterOptions['baseOptions']>;

export type BackgroundOptions = {
  blurRadius?: number;
  imagePath?: string;
  /** cannot be updated through the `update` method, needs a restart */
  segmenterOptions?: SegmenterOptions;
  /** cannot be updated through the `update` method, needs a restart */
  assetPaths?: { tasksVisionFileSet?: string; modelAssetPath?: string };
};

export default class BackgroundProcessor extends VideoTransformer<BackgroundOptions> {
  static get isSupported() {
    return typeof OffscreenCanvas !== 'undefined';
  }

  imageSegmenter?: vision.ImageSegmenter;

  segmentationResults: vision.ImageSegmenterResult | undefined;

  backgroundImage: ImageBitmap | null = null;

  blurRadius?: number;

  /** Индекс класса человека в массиве масок. Уточняется через getLabels() в init(). */
  personClassIndex = DEFAULT_PERSON_CLASS_INDEX;

  /** Сглаженная маска прошлого кадра для временного сглаживания (EMA). */
  prevConfidenceMask?: Float32Array;

  /** Черновик уточнённой маски: эрозия и рампа пишут сюда, без выделений на кадр. */
  private maskRefineScratch?: Float32Array;

  /** Черновик расширенной сердцевины: уверенный человек плюс пиксель наружу. */
  private maskCoreScratch?: Float32Array;

  /** Переиспользуемый канвас мягкой маски 256x256: убирает выделение памяти на каждый кадр. */
  private maskScratchCanvas?: OffscreenCanvas;

  private maskScratchCtx?: OffscreenCanvasRenderingContext2D | null;

  private maskScratchData?: ImageData;

  /** Переиспользуемый полноэкранный канвас вырезанного человека. */
  private personCanvas?: OffscreenCanvas;

  private personCtx?: OffscreenCanvasRenderingContext2D | null;

  /** Низкий канвас заблюренного фона. Обновляется редко, растягивается на полный кадр. */
  private bgLowCanvas?: OffscreenCanvas;

  private bgLowCtx?: OffscreenCanvasRenderingContext2D | null;

  /** Счётчик кадров для каденса обновления фона. */
  private bgFrameCounter = 0;

  options: BackgroundOptions;

  frameCtx: OffscreenCanvasRenderingContext2D;

  frameCanvas: OffscreenCanvas

  constructor(opts: BackgroundOptions) {
    super();
    this.options = opts;
    this.frameCanvas = new OffscreenCanvas(SEGMENTATION_SIZE, SEGMENTATION_SIZE)
    this.frameCtx = this.frameCanvas?.getContext('2d')
    this.update(opts);
  }

  async init({ outputCanvas, inputElement: inputVideo }: VideoTransformerInitOptions) {
    await super.init({ outputCanvas, inputElement: inputVideo });
    const fileSet = await vision.FilesetResolver.forVisionTasks(
      this.options.assetPaths?.tasksVisionFileSet ??
        // `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${packageInfo.dependencies['@mediapipe/tasks-vision']}/wasm`,
        '/processor'
    );
    this.imageSegmenter = await vision.ImageSegmenter.createFromOptions(fileSet, {
      baseOptions: {
        modelAssetPath:
          this.options.assetPaths?.modelAssetPath ??
          '/processor/selfie_segmenter.tflite',
        delegate: 'GPU',
        ...this.options.segmenterOptions,
      },
      runningMode: 'VIDEO',
      outputCategoryMask: true,
      outputConfidenceMasks: true,
    })
    this.resolvePersonClassIndex();
    this.prevConfidenceMask = undefined;
    this.bgFrameCounter = 0;
    this.bgLowCanvas = undefined;
    this.bgLowCtx = undefined;

    // this.loadBackground(opts.backgroundUrl).catch((e) => console.error(e));
  }

  async destroy() {
    await super.destroy();
    await this.imageSegmenter?.close();
    this.prevConfidenceMask = undefined;
    this.maskRefineScratch = undefined;
    this.maskCoreScratch = undefined;
    this.maskScratchCanvas = undefined;
    this.maskScratchCtx = undefined;
    this.maskScratchData = undefined;
    this.personCanvas = undefined;
    this.personCtx = undefined;
    this.bgLowCanvas = undefined;
    this.bgLowCtx = undefined;
    this.bgFrameCounter = 0;
  }

  /**
 * Уточняет индекс класса «человек» по меткам загруженной модели.
 * Без проверки индекс не хардкодим. У моделей с единственной меткой
 * (selfie_segmenter: метка selfie, один выходной канал) этот канал и есть
 * человек — проверено локальным прогоном модели. Иначе остаёмся на значении
 * из карты модели (фон 0, человек 1).
 */
  private resolvePersonClassIndex() {
    const labels = this.imageSegmenter?.getLabels() ?? [];
    const personIndex = labels.findIndex((label) => /person|human|selfie/i.test(label));
    if (personIndex >= 0) {
      this.personClassIndex = personIndex;
      return;
    }
    this.personClassIndex = labels.length === 1 ? 0 : DEFAULT_PERSON_CLASS_INDEX;
  }

  /**
   * Возвращает канвас мягкой сглаженной маски человека (белый — человек).
   * Предпочитает вещественную маску уверенности (плавные края), при её отсутствии
   * откатывается на категорную маску с бинаризацией, чтобы кадр не гас.
   * Канвас переиспользуется между кадрами.
   */
  async getPersonMaskSource(): Promise<CanvasImageSource | undefined> {
    const results = this.segmentationResults;
    if (!results) {
      return undefined;
    }
    const confidence = results.confidenceMasks?.[this.personClassIndex];
    let raw: Float32Array | undefined;
    let width = 0;
    let height = 0;
    if (confidence) {
      raw = confidence.getAsFloat32Array();
      width = confidence.width;
      height = confidence.height;
    } else if (results.categoryMask) {
      const classes = results.categoryMask.getAsUint8Array();
      raw = new Float32Array(classes.length);
      for (let i = 0; i < classes.length; i += 1) {
        raw[i] = classes[i] > 0 ? 1 : 0;
      }
      width = results.categoryMask.width;
      height = results.categoryMask.height;
    } else {
      return undefined;
    }
    const smoothed = this.applyTemporalSmoothing(raw);
    const refined = this.refineMask(smoothed, width, height);
    this.ensureMaskScratch(width, height);
    if (!this.maskScratchCtx || !this.maskScratchData || !this.maskScratchCanvas) {
      return undefined;
    }
    const pixels = this.maskScratchData.data;
    for (let i = 0; i < refined.length; i += 1) {
      const value = Math.round(Math.min(1, Math.max(0, refined[i])) * 255);
      pixels[i * 4] = value;
      pixels[i * 4 + 1] = value;
      pixels[i * 4 + 2] = value;
      pixels[i * 4 + 3] = value;
    }
    this.maskScratchCtx.putImageData(this.maskScratchData, 0, 0);
    return this.maskScratchCanvas;
  }

  /** Создаёт или переиспользует канвас-черновик маски заданного размера. */
  private ensureMaskScratch(width: number, height: number) {
    if (
      !this.maskScratchCanvas ||
      !this.maskScratchCtx ||
      !this.maskScratchData ||
      this.maskScratchCanvas.width !== width ||
      this.maskScratchCanvas.height !== height
    ) {
      this.maskScratchCanvas = new OffscreenCanvas(width, height);
      this.maskScratchCtx = this.maskScratchCanvas.getContext('2d');
      this.maskScratchData = this.maskScratchCtx?.createImageData(width, height);
    }
  }

  /**
   * Вырезает человека кадром в переиспользуемый канвас: маска, затем кадр
   * режимом source-in. Растягивание маленькой маски идёт со сглаживанием —
   * это бесплатное смягчение краёв вместо отдельного блюра маски.
   */
  private cutOutPerson(
    source: CanvasImageSource,
    mask: CanvasImageSource,
  ): OffscreenCanvas | undefined {
    if (!this.canvas) {
      return undefined;
    }
    const { width, height } = this.canvas;
    if (!this.personCanvas || this.personCanvas.width !== width || this.personCanvas.height !== height) {
      this.personCanvas = new OffscreenCanvas(width, height);
      this.personCtx = this.personCanvas.getContext('2d');
    }
    if (!this.personCtx) {
      return undefined;
    }
    this.personCtx.filter = 'none';
    this.personCtx.globalCompositeOperation = 'copy';
    this.personCtx.drawImage(mask, 0, 0, width, height);
    this.personCtx.globalCompositeOperation = 'source-in';
    this.personCtx.drawImage(source, 0, 0, width, height);
    return this.personCanvas;
  }

  /**
   * Обновляет низкий слой заблюренного фона не чаще чем на каждый N-й кадр.
   * Блюр в низком разрешении во много раз дешевле полноэкранного.
   */
  private refreshBackgroundLayer(
    source: CanvasImageSource,
    sourceWidth: number,
    sourceHeight: number,
  ) {
    this.bgFrameCounter += 1;
    if (!this.bgLowCanvas || !this.bgLowCtx) {
      const lowHeight = Math.max(
        1,
        Math.round((BG_LOW_WIDTH * sourceHeight) / Math.max(1, sourceWidth)),
      );
      this.bgLowCanvas = new OffscreenCanvas(BG_LOW_WIDTH, lowHeight);
      this.bgLowCtx = this.bgLowCanvas.getContext('2d');
    }
    if (!this.bgLowCtx || !this.bgLowCanvas) {
      return;
    }
    if (this.bgFrameCounter % BG_UPDATE_EVERY !== 1) {
      return;
    }
    const outputWidth = this.canvas?.width ?? sourceWidth;
    const radius = Math.max(
      1,
      Math.round(((this.blurRadius ?? 12) * this.bgLowCanvas.width) / Math.max(1, outputWidth)),
    );
    this.bgLowCtx.filter = `blur(${radius}px)`;
    this.bgLowCtx.globalCompositeOperation = 'copy';
    this.bgLowCtx.drawImage(source, 0, 0, this.bgLowCanvas.width, this.bgLowCanvas.height);
    this.bgLowCtx.filter = 'none';
  }

  /**
   * Временное сглаживание маски (EMA): подмешивает память прошлого кадра,
   * чтобы маска не дрожала. При движении (маска сильно сдвинулась) вес свежего
   * кадра выше, иначе за рукой тянется шлейф резкого фона. Первый кадр как есть.
   */
  private applyTemporalSmoothing(current: Float32Array): Float32Array {
    const prev = this.prevConfidenceMask;
    const smoothed = new Float32Array(current.length);
    if (!prev || prev.length !== current.length) {
      for (let i = 0; i < current.length; i += 1) {
        smoothed[i] = Math.min(1, Math.max(0, current[i]));
      }
    } else {
      let diff = 0;
      for (let i = 0; i < current.length; i += 1) {
        diff += Math.abs(Math.min(1, Math.max(0, current[i])) - prev[i]);
      }
      const factor =
        diff / current.length > MASK_MOTION_THRESHOLD
          ? MASK_SMOOTHING_MOTION_FACTOR
          : MASK_SMOOTHING_FACTOR;
      for (let i = 0; i < current.length; i += 1) {
        const clamped = Math.min(1, Math.max(0, current[i]));
        smoothed[i] = factor * clamped + (1 - factor) * prev[i];
      }
    }
    this.prevConfidenceMask = smoothed;
    return smoothed;
  }

  /**
   * Уточнение маски: эрозия 3x3 срезает лишний пиксель по краю (резкая кайма
   * фона вокруг человека), затем уверенная сердцевина расширяется обратно на
   * пиксель — край лица, где модель уверена, восстанавливается, а сомнительная
   * кайма остаётся срезанной. В конце smoothstep-рампа уплотняет середину,
   * а финальное усреднение 3x3 плавит лесенку жёстких операций, иначе край
   * идёт пиксельной сеткой при растягивании маски на полный кадр.
   * Край остаётся мягким за счёт растягивания маленькой маски при вырезании.
   */
  private refineMask(mask: Float32Array, width: number, height: number): Float32Array {
    if (!this.maskRefineScratch || this.maskRefineScratch.length !== mask.length) {
      this.maskRefineScratch = new Float32Array(mask.length);
    }
    if (!this.maskCoreScratch || this.maskCoreScratch.length !== mask.length) {
      this.maskCoreScratch = new Float32Array(mask.length);
    }
    const out = this.maskRefineScratch;
    const core = this.maskCoreScratch;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        let min = 1;
        let maxCore = 0;
        for (let ky = -1; ky <= 1; ky += 1) {
          const yy = Math.min(height - 1, Math.max(0, y + ky));
          for (let kx = -1; kx <= 1; kx += 1) {
            const xx = Math.min(width - 1, Math.max(0, x + kx));
            const value = mask[yy * width + xx];
            if (value < min) {
              min = value;
            }
            const coreValue = Math.min(
              1,
              Math.max(0, (value - MASK_CORE_LO) / (MASK_CORE_HI - MASK_CORE_LO)),
            );
            if (coreValue > maxCore) {
              maxCore = coreValue;
            }
          }
        }
        out[y * width + x] = min;
        core[y * width + x] = maxCore;
      }
    }
    for (let i = 0; i < out.length; i += 1) {
      const t = Math.min(1, Math.max(0, (out[i] - MASK_LO) / (MASK_HI - MASK_LO)));
      const soft = t * t * (3 - 2 * t);
      out[i] = Math.max(soft, core[i]);
    }
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        let sum = 0;
        for (let ky = -1; ky <= 1; ky += 1) {
          const yy = Math.min(height - 1, Math.max(0, y + ky));
          for (let kx = -1; kx <= 1; kx += 1) {
            sum += out[yy * width + Math.min(width - 1, Math.max(0, x + kx))];
          }
        }
        core[y * width + x] = sum / 9;
      }
    }
    return core;
  }

  async loadBackground(path: string) {
    const img = new Image();

    await new Promise((resolve, reject) => {
      img.crossOrigin = 'Anonymous';
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = path;
    });
    const imageData = await createImageBitmap(img);
    this.backgroundImage = imageData;
  }

  async transform(frame: VideoFrame, controller: TransformStreamDefaultController<VideoFrame>) {
    try {
      if (this.isDisabled) {
        controller.enqueue(frame);
        return;
      }
      if (!this.canvas) {
        throw TypeError('Canvas needs to be initialized first');
      }
      await this.processSource(frame, frame.displayWidth, frame.displayHeight);
      const newFrame = new VideoFrame(this.canvas, {
        timestamp: frame.timestamp || Date.now(),
      });
      controller.enqueue(newFrame);
    } finally {
      frame.close();
    }
  }

  /**
   * Чистая функция компоновки кадра: сегментация входного источника и склейка
   * результата в выходной канвас. Общий код обоих путей: процессорного (transform)
   * и пути захвата канваса (Firefox). Картинка обязана совпадать.
   */
  async processSource(
    source: CanvasImageSource,
    sourceWidth: number,
    sourceHeight: number,
  ): Promise<void> {
    if (!this.canvas) {
      throw TypeError('Canvas needs to be initialized first');
    }
    this.frameCtx.drawImage(
      this.inputVideo,
      0,
      0,
      this.frameCanvas.width,
      this.frameCanvas.height,
    );
    const startTimeMs = performance.now();

    this.imageSegmenter?.segmentForVideo(
      //@ts-ignore
      this.frameCanvas,
      startTimeMs,
      (result) => (this.segmentationResults = result),
    );

    if (this.blurRadius) {
      await this.blurBackground(source, sourceWidth, sourceHeight);
    } else {
      await this.drawVirtualBackground(source);
    }
  }

  async update(opts: BackgroundOptions) {
    this.options = opts;
    if (opts.blurRadius) {
      this.blurRadius = opts.blurRadius;
    } else if (opts.imagePath) {
      await this.loadBackground(opts.imagePath);
      this.blurRadius = null
    }
  }

  async drawVirtualBackground(source: CanvasImageSource) {
    if (!this.canvas || !this.ctx || !this.segmentationResults || !this.inputVideo) return;
    const mask = await this.getPersonMaskSource();
    if (!mask) {
      this.ctx.drawImage(source, 0, 0, this.canvas.width, this.canvas.height);
      return;
    }
    this.ctx.filter = 'none';
    this.ctx.globalCompositeOperation = 'copy';
    if (this.backgroundImage) {
      drawImageCover(this.ctx, this.backgroundImage, 0, 0, this.canvas.width, this.canvas.height);
    } else {
      this.ctx.fillStyle = '#000000';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
    const person = this.cutOutPerson(source, mask);
    this.ctx.globalCompositeOperation = 'source-over';
    if (person) {
      this.ctx.drawImage(person, 0, 0, this.canvas.width, this.canvas.height);
    }
  }

  async blurBackground(source: CanvasImageSource, sourceWidth: number, sourceHeight: number) {
    if (!this.ctx || !this.canvas || !this.segmentationResults || !this.inputVideo) {
      return;
    }
    const mask = await this.getPersonMaskSource();
    this.refreshBackgroundLayer(source, sourceWidth, sourceHeight);
    if (!mask || !this.bgLowCanvas) {
      return;
    }
    this.ctx.filter = 'none';
    this.ctx.globalCompositeOperation = 'copy';
    this.ctx.drawImage(this.bgLowCanvas, 0, 0, this.canvas.width, this.canvas.height);
    const person = this.cutOutPerson(source, mask);
    this.ctx.globalCompositeOperation = 'source-over';
    if (person) {
      this.ctx.drawImage(person, 0, 0, this.canvas.width, this.canvas.height);
    }
  }
}

function drawImageCover(ctx, img, x, y, w, h) {
  // Вычисляем соотношения сторон холста и изображения
  const canvasRatio = w / h;
  const imgRatio = img.width / img.height;

  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = img.width;
  let sourceHeight = img.height;
  let destWidth = w;
  let destHeight = h;

  // Если соотношение сторон холста больше, чем у изображения (холст шире)
  if (canvasRatio > imgRatio) {
    sourceWidth = img.width;
    sourceHeight = img.width * (h / w);
    sourceY = (img.height - sourceHeight) / 2;
  } 
  // Иначе (холст выше)
  else {
    sourceWidth = img.height * (w / h);
    sourceHeight = img.height;
    sourceX = (img.width - sourceWidth) / 2;
  }

  // Рисуем изображение на холсте
  ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, x, y, destWidth, destHeight);
}
